// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useSharedMqtt } from './SharedMqttProvider';
import { convertToUTC } from '../utils/timezoneUtils';

const MqttDebugContext = createContext();

export function useMqttDebug() {
  return useContext(MqttDebugContext);
}

export function MqttDebugProvider({ children }) {
  const MAX_MESSAGE_BUFFER = 500;

  // Version for sessionStorage - increment when data format changes to auto-clear old data
  const STORAGE_VERSION = '6';

  // State for MQTT messages
  const [messages, setMessages] = useState([]);
  const [isListening, setIsListening] = useState(true);
  const isListeningRef = useRef(isListening);
  const saveTimerRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [mqttError, setMqttError] = useState(null);
  const [lastConnectionAttempt, setLastConnectionAttempt] = useState(null);
  const [connectionRetryCount, setConnectionRetryCount] = useState(0);

  // IMPROVED: Simplified health monitoring - SharedMqttProvider now handles most of this
  const lastMessageTimeRef = useRef(Date.now());
  const messageCountRef = useRef(0);
  const wasConnectedRef = useRef(false);

  // IMPROVED: Use listener ID instead of function reference for cleanup
  const listenerIdRef = useRef(null);
  const isListenerActiveRef = useRef(false);

  // PERFORMANCE: Batch incoming messages to reduce React re-renders.
  // Instead of calling setMessages for every single message, we queue them
  // and flush the queue on a microtask boundary (16ms ≈ one animation frame).
  const pendingMessagesRef = useRef([]);
  const flushTimerRef = useRef(null);

  // Per-type rate limiting to prevent one type from flooding the console.
  // A single device publishes ~11 HEALTH messages per heartbeat cycle (~10s).
  // With multiple devices the count scales linearly, so the limit must be generous.
  const typeRateLimiterRef = useRef(new Map());
  const RATE_LIMIT_WINDOW_MS = 10000;
  const MAX_PER_TYPE_PER_WINDOW = 60;

  // Use shared MQTT connection
  const sharedMqtt = useSharedMqtt();
  const client = sharedMqtt?.client || null;
  const mqttConnected = sharedMqtt?.isConnected || false;
  const messageFlowHealthy = sharedMqtt?.messageFlowHealthy ?? true;
  const sharedMqttError = sharedMqtt?.connectionError || null;
  const addMessageListener = sharedMqtt?.addMessageListener || (() => null);
  const removeMessageListener = sharedMqtt?.removeMessageListener || (() => false);
  const forceReconnect = sharedMqtt?.forceReconnect || (() => {});

  // NOTE: Dashboard heartbeats are published by the backend MQTT service (single worker).
  // Removed frontend useMqttHeartbeat hook to avoid duplicate heartbeat messages.
  // The backend publishes to starfleet/heartbeat/dashboard every 10 seconds.

  // Track when we last cleared messages (to detect logout/login cycles)
  const lastClearTimeRef = useRef(Date.now());
  const wasAuthenticatedRef = useRef(false);

  // Methods to manipulate messages (defined early so they can be used in effects)
  const clearMessages = React.useCallback(() => {
    setMessages([]);
    sessionStorage.removeItem('mqttDebugMessages');
    lastClearTimeRef.current = Date.now();
    sessionStorage.setItem('mqttDebugLastClear', lastClearTimeRef.current.toString());
  }, []);

  // Load saved messages from sessionStorage on initial load
  useEffect(() => {
    try {
      const savedVersion = sessionStorage.getItem('mqttDebugVersion');
      const savedMessages = sessionStorage.getItem('mqttDebugMessages');
      const lastClearTimeStr = sessionStorage.getItem('mqttDebugLastClear');

      // Update lastClearTimeRef from storage if available
      if (lastClearTimeStr) {
        lastClearTimeRef.current = parseInt(lastClearTimeStr, 10);
      }

      // Keep messages across refreshes for up to 60 minutes — the debug console is a log
      const messagesAreStale =
        lastClearTimeStr && Date.now() - parseInt(lastClearTimeStr, 10) > 60 * 60 * 1000;

      // Only load if version matches, messages exist, and they're not ancient
      if (savedVersion === STORAGE_VERSION && savedMessages && !messagesAreStale) {
        const parsedMessages = JSON.parse(savedMessages);
        if (parsedMessages.length > 0) {
          setMessages(parsedMessages);
        }
      } else {
        // Clear old data if version doesn't match or messages are stale
        sessionStorage.removeItem('mqttDebugMessages');
        sessionStorage.setItem('mqttDebugVersion', STORAGE_VERSION);
        // Update clear time since we're clearing
        lastClearTimeRef.current = Date.now();
        sessionStorage.setItem('mqttDebugLastClear', lastClearTimeRef.current.toString());
      }
    } catch (error) {
      console.error('Error loading saved MQTT messages:', error);
      // If there's an error (e.g., corrupt data), clear the storage
      sessionStorage.removeItem('mqttDebugMessages');
      sessionStorage.removeItem('mqttDebugVersion');
      sessionStorage.setItem('mqttDebugVersion', STORAGE_VERSION);
      // Update clear time since we're clearing
      lastClearTimeRef.current = Date.now();
      sessionStorage.setItem('mqttDebugLastClear', lastClearTimeRef.current.toString());
    }
  }, []);

  // Debounced save to sessionStorage — batches writes to avoid blocking the main thread
  // with JSON.stringify on every single incoming message.
  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      try {
        const serialized = JSON.stringify(messages);
        sessionStorage.setItem('mqttDebugMessages', serialized);
        sessionStorage.setItem('mqttDebugVersion', STORAGE_VERSION);
      } catch {
        // sessionStorage full — clear persisted copy, in-memory array stays intact
        try {
          sessionStorage.removeItem('mqttDebugMessages');
        } catch {
          /* ignore */
        }
      }
    }, 2000); // Write at most once every 2 seconds
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [messages]);

  // Flush pending messages into React state in a single batch.
  // This avoids calling setMessages for every individual MQTT message.
  // Uses fair-share trimming: when the buffer is full, ensure each message type
  // retains at least MIN_PRESERVED_PER_TYPE of its most recent messages so that
  // a high-frequency type cannot completely push out lower-frequency types.
  const MIN_PRESERVED_PER_TYPE = 80;

  const flushPendingMessages = useCallback(() => {
    flushTimerRef.current = null;
    const batch = pendingMessagesRef.current;
    if (batch.length === 0) return;
    pendingMessagesRef.current = [];

    setMessages((prev) => {
      const combined = prev.concat(batch);
      if (combined.length <= MAX_MESSAGE_BUFFER) {
        return combined;
      }

      // Fair-share trim: guarantee each type keeps its most recent N messages,
      // then fill remaining slots with the newest messages overall.
      const byType = new Map();
      for (let i = 0; i < combined.length; i++) {
        const msg = combined[i];
        if (!byType.has(msg.type)) {
          byType.set(msg.type, []);
        }
        byType.get(msg.type).push(i); // store indices to preserve order
      }

      // Collect indices of messages that are guaranteed to be kept
      const preserved = new Set();
      for (const indices of byType.values()) {
        const start = Math.max(0, indices.length - MIN_PRESERVED_PER_TYPE);
        for (let j = start; j < indices.length; j++) {
          preserved.add(indices[j]);
        }
      }

      // Fill remaining slots from newest to oldest (excluding already-preserved)
      const remaining = MAX_MESSAGE_BUFFER - preserved.size;
      if (remaining > 0) {
        let added = 0;
        for (let i = combined.length - 1; i >= 0 && added < remaining; i--) {
          if (!preserved.has(i)) {
            preserved.add(i);
            added++;
          }
        }
      }

      // Build result in original chronological order
      const result = [];
      for (let i = 0; i < combined.length; i++) {
        if (preserved.has(i)) {
          result.push(combined[i]);
        }
      }

      // Cap at MAX_MESSAGE_BUFFER (safety net)
      if (result.length > MAX_MESSAGE_BUFFER) {
        return result.slice(result.length - MAX_MESSAGE_BUFFER);
      }
      return result;
    });
  }, []);

  // Helper: build a formatted message object from a raw MQTT message.
  // This runs synchronously in the message callback — no React state touched.
  const formatMessage = useCallback((msg) => {
    const topicParts = msg.topic ? msg.topic.split('/') : [];
    let serialNumber = null;
    if (topicParts.length >= 2) {
      const candidateParts = topicParts.filter((p) => /^\d+$/.test(p));
      if (candidateParts.length > 0) {
        serialNumber = candidateParts[0];
      } else if (topicParts.length > 2) {
        const lastPart = topicParts[2];
        serialNumber =
          lastPart.startsWith('stargate_') || lastPart.startsWith('sg_')
            ? lastPart.replace(/^(stargate_|sg_)/, '')
            : lastPart;
      }
    }

    const topic = msg.topic || '';
    let messageType = msg.type || 'HEALTH';
    if (topic.startsWith('wireguard/')) messageType = 'NETWORK_METRICS';
    else if (topic.includes('/heartbeat/')) messageType = 'DASHBOARD_HEARTBEAT';
    else if (topic.includes('/status/')) messageType = 'HEALTH';
    else if (topic.includes('/command/') || topic.includes('/response/')) messageType = 'COMMAND';
    else if (topic.includes('/disconnect/')) messageType = 'DISCONNECT';

    let payloadStr;
    try {
      if (typeof msg === 'object') {
        const fullJson = JSON.stringify(msg, null, 2);
        if (fullJson.length > 2048) {
          const summary = {};
          for (const key of Object.keys(msg)) {
            const val = msg[key];
            if (val == null) continue;
            if (typeof val === 'object' && !Array.isArray(val)) {
              summary[key] = `{${Object.keys(val).length} fields}`;
            } else if (Array.isArray(val)) {
              summary[key] = `[${val.length} items]`;
            } else {
              summary[key] = val;
            }
          }
          payloadStr = JSON.stringify(summary, null, 2);
        } else {
          payloadStr = fullJson;
        }
      } else {
        payloadStr = String(msg);
      }
    } catch {
      payloadStr = String(msg);
    }

    return {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: convertToUTC(msg.timestamp),
      topic: topic || 'Unknown Topic',
      type: messageType,
      stargateId: serialNumber || 'unknown',
      payload: payloadStr,
    };
  }, []);

  // IMPROVED: Stable message handler that batches incoming messages.
  // Pushes formatted messages into a queue and schedules a single flush per animation frame.
  // Includes per-type rate limiting so no single message type can flood the console.
  const handleMessage = useCallback(
    (msg) => {
      try {
        if (!isListeningRef.current) return;

        lastMessageTimeRef.current = Date.now();
        messageCountRef.current += 1;

        const formatted = formatMessage(msg);

        // Per-type rate limiting: cap how many messages of one type are accepted per window.
        // This prevents high-frequency types (e.g. NETWORK_METRICS from many WireGuard peers)
        // from pushing all other types out of the buffer over time.
        const now = Date.now();
        const limiter = typeRateLimiterRef.current;
        let info = limiter.get(formatted.type);

        if (!info || now - info.windowStart >= RATE_LIMIT_WINDOW_MS) {
          // Start a fresh window
          info = { count: 0, windowStart: now };
          limiter.set(formatted.type, info);
        }

        info.count++;

        if (info.count > MAX_PER_TYPE_PER_WINDOW) {
          // Drop this message to keep the console balanced across types
          return;
        }

        pendingMessagesRef.current.push(formatted);

        // Schedule flush if not already scheduled (coalesce within ~50ms)
        if (!flushTimerRef.current) {
          flushTimerRef.current = setTimeout(flushPendingMessages, 50);
        }
      } catch (err) {
        console.error('[MqttDebugContext] Error processing MQTT message:', err);
      }
    },
    [formatMessage, flushPendingMessages]
  ); // Stable deps via useCallback

  // IMPROVED: Register/unregister listener using ID-based system
  useEffect(() => {
    // Don't register if not listening
    if (!isListening) {
      // Unregister existing listener if any
      if (listenerIdRef.current) {
        removeMessageListener(listenerIdRef.current);
        listenerIdRef.current = null;
        isListenerActiveRef.current = false;
      }
      return;
    }

    // Register the listener as PERMANENT — it must never be cleaned up as stale
    const listenerId = addMessageListener(handleMessage, 'MqttDebugContext', {
      permanent: true,
    });

    if (listenerId) {
      listenerIdRef.current = listenerId;
      isListenerActiveRef.current = true;
      lastMessageTimeRef.current = Date.now();
      messageCountRef.current = 0;
    } else {
      console.error('[MqttDebugContext] Failed to register listener');
    }

    // Cleanup: remove listener on unmount or when listening state changes
    return () => {
      if (listenerIdRef.current) {
        console.log(`[MqttDebugContext] Cleanup: removing listener ${listenerIdRef.current}`);
        removeMessageListener(listenerIdRef.current);
        listenerIdRef.current = null;
        isListenerActiveRef.current = false;
      }
    };
  }, [isListening, handleMessage, addMessageListener, removeMessageListener]);

  // IMPROVED: Simplified health monitoring - SharedMqttProvider handles most of this now
  // This effect just monitors from the consumer side and triggers recovery if needed
  useEffect(() => {
    if (!isListening || !mqttConnected) {
      return;
    }

    // Reset timing when connection is established
    lastMessageTimeRef.current = Date.now();

    // Health check interval - check if we're receiving messages
    // This is a backup to SharedMqttProvider's health monitoring
    const healthCheckInterval = setInterval(() => {
      const timeSinceLastMessage = Date.now() - lastMessageTimeRef.current;

      // If SharedMqttProvider says message flow is unhealthy, trust it
      if (!messageFlowHealthy) {
        console.warn('[MqttDebugContext] Message flow reported unhealthy by SharedMqttProvider');
        return; // SharedMqttProvider will handle recovery
      }

      // If we haven't received messages for 90 seconds and we have a listener registered,
      // there might be an issue with our specific listener
      if (timeSinceLastMessage > 90000 && listenerIdRef.current) {
        console.warn(
          `[MqttDebugContext] No messages received for ${Math.round(timeSinceLastMessage / 1000)}s, re-registering listener...`
        );

        // Remove old listener
        removeMessageListener(listenerIdRef.current);
        listenerIdRef.current = null;

        // Register new listener (permanent — immune to stale cleanup)
        const newListenerId = addMessageListener(handleMessage, 'MqttDebugContext_recovery', {
          permanent: true,
        });
        if (newListenerId) {
          listenerIdRef.current = newListenerId;
          lastMessageTimeRef.current = Date.now();
          console.log(`[MqttDebugContext] Re-registered listener with new ID: ${newListenerId}`);
        }
      }
    }, 15000); // Check every 15 seconds

    return () => {
      clearInterval(healthCheckInterval);
    };
  }, [
    isListening,
    mqttConnected,
    messageFlowHealthy,
    handleMessage,
    addMessageListener,
    removeMessageListener,
  ]);

  // Listen for logout events to clear messages and pending buffers
  useEffect(() => {
    const handleLogout = () => {
      clearMessages();
      pendingMessagesRef.current = [];
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      typeRateLimiterRef.current.clear();
    };

    window.addEventListener('user-logout', handleLogout);
    window.addEventListener('session-timeout', handleLogout);

    return () => {
      window.removeEventListener('user-logout', handleLogout);
      window.removeEventListener('session-timeout', handleLogout);
    };
  }, [clearMessages]);

  // Update connection status based on shared MQTT connection
  useEffect(() => {
    const wasConnected = wasConnectedRef.current;
    const nowConnected = mqttConnected;

    setConnectionStatus(mqttConnected ? 'connected' : 'disconnected');
    setMqttError(sharedMqttError);
    setConnectionRetryCount(0); // Reset since shared connection handles retries

    // On reconnect, just reset health counters — NEVER clear messages.
    // Messages are a valuable log; they should only be cleared by explicit user action or logout.
    if (!wasConnected && nowConnected) {
      lastMessageTimeRef.current = Date.now();
      messageCountRef.current = 0;
    }

    // Update ref for next comparison
    wasConnectedRef.current = nowConnected;
  }, [mqttConnected, sharedMqttError]);

  const toggleListening = () => {
    setIsListening((prev) => {
      const newValue = !prev;
      isListeningRef.current = newValue;
      return newValue;
    });
  };

  // Provide all required state and methods to consumers
  // Get a more detailed connection status message
  const getConnectionStatusMessage = () => {
    if (connectionStatus === 'connected') return 'Connected to MQTT broker';
    if (connectionStatus.startsWith('error')) return 'Connection error';
    if (connectionStatus.includes('reconnecting')) return connectionStatus;
    return 'Disconnected';
  };

  // Get a more detailed error message with troubleshooting tips
  const getDetailedErrorMessage = () => {
    if (!sharedMqttError) return null;

    let message = sharedMqttError;
    let details = '';

    if (sharedMqttError.includes('timed out')) {
      details =
        'The MQTT broker did not respond in time. Please check if the broker is running and accessible.';
    } else if (sharedMqttError.includes('ECONNREFUSED')) {
      details =
        'The connection was refused. This usually means the MQTT broker is not running at the specified address or port.';
    } else if (sharedMqttError.includes('WebSocket connection failed')) {
      details =
        'Failed to establish WebSocket connection. Make sure the MQTT broker supports WebSockets and CORS is properly configured.';
    } else if (sharedMqttError.includes('Not authorized')) {
      details = 'Authentication failed. Please verify your MQTT username and password.';
    }

    // Return a formatted string instead of an object
    return details ? `${message} - ${details}` : message;
  };

  // IMPROVED: Manual recovery function that can be called from UI
  const forceRecovery = useCallback(() => {
    // First, re-register our listener
    if (listenerIdRef.current) {
      removeMessageListener(listenerIdRef.current);
      listenerIdRef.current = null;
    }

    // Register new listener (permanent — immune to stale cleanup)
    const newListenerId = addMessageListener(handleMessage, 'MqttDebugContext_manual_recovery', {
      permanent: true,
    });
    if (newListenerId) {
      listenerIdRef.current = newListenerId;
      lastMessageTimeRef.current = Date.now();
      messageCountRef.current = 0;
      console.log(`[MqttDebugContext] Recovery: registered new listener ${newListenerId}`);
    }

    // Also trigger a full reconnect for good measure
    if (forceReconnect && typeof forceReconnect === 'function') {
      setTimeout(() => {
        forceReconnect({ clearStaleListeners: true });
      }, 500);
    }
  }, [handleMessage, addMessageListener, removeMessageListener, forceReconnect]);

  return (
    <MqttDebugContext.Provider
      value={useMemo(
        () => ({
          messages,
          clearMessages,
          isListening,
          toggleListening,
          connectionStatus: getConnectionStatusMessage(),
          connectionDetails: connectionStatus,
          mqttError: getDetailedErrorMessage(),
          mqttErrorDetails: sharedMqttError,
          lastConnectionAttempt,
          retryCount: connectionRetryCount,
          isConnected: connectionStatus === 'connected',
          reconnect: forceRecovery,
          forceRecovery,
        }),
        [
          messages,
          clearMessages,
          isListening,
          toggleListening,
          connectionStatus,
          sharedMqttError,
          lastConnectionAttempt,
          connectionRetryCount,
          forceRecovery,
        ]
      )}
    >
      {children}
    </MqttDebugContext.Provider>
  );
}
